<template>
  <!--
    Video.js mutates/reparents the <video> subtree. If that <video> is template-driven,
    Vue's vnode no longer matches the DOM and patching throws (insertBefore errors).
    Only this empty wrapper is Vue-managed; the <video> is created imperatively.
  -->
  <div ref="hostRef" class="uc-videojs-tech-host w-full h-full min-h-0" />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, inject, watch } from 'vue';

const props = defineProps<{
  dataTest?: string | null;
  videoClass?: string | Record<string, boolean>;
}>();

const hostRef = ref<HTMLDivElement | null>(null);
const videoEl = ref<HTMLVideoElement | null>(null);

function syncVideoAttrs(el: HTMLVideoElement) {
  el.classList.add('video-js');
  if (props.dataTest) el.setAttribute('data-test', props.dataTest);
  else el.removeAttribute('data-test');

  const vc = props.videoClass;
  if (!vc || typeof vc === 'string') {
    if (typeof vc === 'string') {
      for (const c of vc.split(/\s+/).filter(Boolean)) el.classList.add(c);
    }
    return;
  }
  for (const [name, on] of Object.entries(vc)) {
    if (on) el.classList.add(name);
    else el.classList.remove(name);
  }
}

function createVideoInHost() {
  const host = hostRef.value;
  if (!host || videoEl.value) return;

  const el = document.createElement('video');
  el.setAttribute('playsinline', 'true');
  el.setAttribute('crossorigin', 'anonymous');
  syncVideoAttrs(el);
  host.appendChild(el);
  videoEl.value = el;
}

onMounted(() => {
  createVideoInHost();
});

watch(
  () => props.dataTest,
  () => {
    if (videoEl.value) syncVideoAttrs(videoEl.value);
  }
);

watch(
  () => props.videoClass,
  () => {
    if (videoEl.value) syncVideoAttrs(videoEl.value);
  },
  { deep: true }
);

defineExpose({ videoEl });

const onTechUnmount = inject<() => void>('ucVideoTechUnmount', () => {});

onBeforeUnmount(() => {
  onTechUnmount();
  const host = hostRef.value;
  if (host) host.replaceChildren();
  videoEl.value = null;
});
</script>
